<template>
  <div>
    <div class="content-header">
      <h1>Plán rozvozu</h1>
    </div>
    <section class="content">
      <div class="box">
        <div class="box-body">
          <div class="row">
            <div class="col-md-12">
              <order-create-modal :show-model="showCreateModal" :model="selectedOrder" @close="hideModals()"></order-create-modal>
              <div class="pull-left">
                <div class="form-group inline-block">
                  <date-select v-model="date"></date-select>
                </div>
                <div class="inline-block">
                  <router-link tag="button" to="/orders/print" class="btn btn-primary">Tlač</router-link>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-3 col-sm-6 col-xs-6">
              <select-field field-id="area"
                            placeholder="Oblasť"
                            :options="validAreas"
                            v-model="selectedArea"></select-field>
            </div>
            <div class="col-md-3 col-sm-6 col-xs-6">
              <text-field field-id="customer"
                          placeholder="Vyhľadať zákazníka"
                          v-model="query"
                          :maxlength="100"></text-field>
            </div>
          </div>
          <div class="row" v-if="!loading">
            <div class="col-md-12">
              <table class="table table-hover">
                <thead>
                <tr>
                  <th>Odberateľ</th>
                  <th v-for="product in validProducts" class="text-nowrap">{{ product.label }}</th>
                  <th>Hotovosť</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <template v-for="customer in customers">
                  <tr>
                    <td>{{ customer.companyName }}</td>
                    <td v-for="product in validProducts"><span title="Počet objednaných">{{ amount(customer.id, product.value) }}</span> / <span title="Počet vratiek">{{ returns(customer.id, product.value) }}</span></td>
                    <td>{{ cash(customer.id) | currency }}</td>
                    <td class="text-right text-nowrap">
                      <button class="btn btn-xs btn-link" v-if="hasSomeRole(['DEALER', 'DEALER_L2'])" :disabled="!customer.deliveryNote" @click.prevent="printDeliveryNote(customer)"><i class="fa fa-2x fa-print text-success" title="Vytlačiť dodací list"></i></button>
                      <button class="btn btn-xs btn-link" v-if="isEditEnabled(customer)" @click.prevent="editOrder(customer)"><i class="fa fa-2x fa-pencil-square-o text-primary" title="Editovať"></i></button>
                    </td>
                  </tr>
                </template>
                </tbody>
                <tfoot>
                  <tr class="text-bold">
                    <td>Suma</td>
                    <td v-for="product in validProducts"><span title="Počet objednaných">{{ amountSum(product.value) }}</span> / <span title="Počet vratiek">{{ returnsSum(product.value) }}</span></td>
                    <td>{{ cashSum() | currency }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <loading id="orders"></loading>
          <no-records :data="customers" loadingId="orders"></no-records>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import _ from 'lodash';
  import { mapState, mapGetters } from 'vuex';
  import loading from 'components/loading';
  import noRecords from 'components/noRecords';
  import selectField from 'components/form/selectField';
  import textField from 'components/form/textField';
  import orderService from 'services/orderService';
  import orderCreateModal from 'components/orderCreateModal';
  import dateSelect from 'components/dateSelect';

  export default {
    components: { selectField, textField, orderCreateModal, dateSelect, mapGetters, loading, noRecords },
    data: () => ({
      showCreateModal: false,
      selectedArea: null,
      selectedOrder: null,
      query: null,
      date: null
    }),
    computed: {
      ...mapGetters({
        validAreas: 'area/validItems',
        validProducts: 'product/validItems',
        hasSomeRole: 'auth/hasSomeRole',
        hasRole: 'auth/hasRole'
      }),
      ...mapState({
        customers: (state, getters) => _(getters['customer/filteredItems']).sortBy((customer) => customer.sortOrder).value(),
        orders(state) {
          return state.order.items;
        },
        loading: (state) => state.app.loadingData.orders //must be here because cant do "if" on empty orders
      })
    },
    methods: {
      amount(customerId, productId) {
        const orderItem = this.orderItem(customerId, productId);
        return orderItem ? orderItem.amount : 0;
      },
      amountSum(productId) {
        return _(this.visibleOrderItemsForProduct(productId)).map('amount').sum();
      },
      returnsSum(productId) {
        return _(this.visibleOrderItemsForProduct(productId)).map('returns').sum();
      },
      returns(customerId, productId) {
        const orderItem = this.orderItem(customerId, productId);
        return orderItem ? orderItem.returns : 0;
      },
      cash(customerId) {
        const order = this.order(customerId);
        return order ? order.cash || 0 : 0;
      },
      cashSum() {
        return _(this.visibleOrderItems()).map('cash').sum();
      },
      order(customerId) {
        return this.orders.find(order => order.customer.id === customerId);
      },
      orderItem(customerId, productId) {
        const order = this.order(customerId);
        if (order) {
          const orderItem = order.items.find(orderItem => orderItem.product.id === productId);
          if (orderItem) {
            return orderItem;
          }
        }
        return null;
      },
      visibleOrderItems() {
        let customerIds = _.map(this.customers, 'id');
        return _(this.orders).filter(order => customerIds.indexOf(order.customer.id) > -1).values();
      },
      visibleOrderItemsForProduct(productId) {
        return _(this.visibleOrderItems()).flatMap('items').filter(item => item.product.id === productId).values();
      },
      //edit is enabled for DEALERs and also for DRIVERs but only for edit mode, not create mode
      isEditEnabled(customer) {
        return this.hasSomeRole(['DEALER', 'DEALER_L2']) || (this.order(customer.id) && this.hasRole('DRIVER'));
      },
      editOrder(customer) {
        let order = this.order(customer.id);
        if (!order) {
            order = orderService.newOrder(customer, this.date.startOf('d'), this.validProducts);
        }
        this.selectedOrder = _.cloneDeep(order);
        this.showCreateModal = true;
      },
      printDeliveryNote(customer) {
        this.$store.dispatch('deliveryNote/setFilterCustomer', customer.id);
        this.$store.dispatch('deliveryNote/setFilterDate', this.date);
        this.$router.push('/delivery-notes/print');
      },
      hideModals () {
        this.showCreateModal = false;
      },
      fetch() {
        this.$store.dispatch('order/findByDate', this.date);
      }
    },
    watch: {
      date() {
        this.fetch();
      },
      selectedArea(selectedArea) {
          this.$store.dispatch('customer/setFilterArea', selectedArea);
      },
      query(query) {
        this.$store.dispatch('customer/setFilterCompanyName', query);
      }
    },
    created () {
      this.$store.dispatch('area/getAll');
      this.$store.dispatch('customer/getAll');
      this.$store.dispatch('product/getAll');
    },
    mounted () {
      this.selectedArea = this.$store.state.customer.filter.area;
      this.query = this.$store.state.customer.filter.companyName;
      this.date = this.$store.state.order.filterDate;
    }
  }
</script>



// WEBPACK FOOTER //
// client/views/orders.vue